import React, { useState, useEffect } from 'react';
import { getProjects, createProject, deleteProject, joinWaitlist } from '../api';
import { useToast } from '../App';
import { useAuth } from '../context/AuthContext';
import ProjectCard from '../components/ProjectCard';
import { Layers, ListChecks, Zap, Folder, Plus } from '../components/Icons';
import './Dashboard.css';

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newClient, setNewClient] = useState('');
  const [creating, setCreating] = useState(false);
  const { addToast } = useToast();
  const { user, profile, planMeta } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [submittingUpgrade, setSubmittingUpgrade] = useState(false);

  const currentPlan = profile?.plan || 'starter';
  const targetPlan = currentPlan === 'starter' ? 'pro' : 'teams';
  const targetLabel = currentPlan === 'starter' ? 'Pro — $12/mo' : 'Teams — $29/mo';

  async function handleUpgradeRequest() {
    setSubmittingUpgrade(true);
    try {
      await joinWaitlist({ email: user?.email, plan: targetPlan, source: 'in-app-dashboard' });
    } catch { /* silent — waitlist may already have this email */ } finally {
      setSubmittingUpgrade(false);
      setShowUpgradeModal(false);
      addToast(`Got it! We'll reach out to ${user?.email} to complete your upgrade.`, 'success');
    }
  }

  const fetchProjects = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProjects();
      setProjects(data);
    } catch {
      // Silently fail — the empty state UI handles the no-projects case
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const project = await createProject({ projectName: newName.trim(), clientName: newClient.trim() });
      setProjects(prev => [project, ...prev]);
      setShowNewModal(false);
      setNewName(''); setNewClient('');
      addToast(`"${project.projectName}" created!`, 'success');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create project';
      addToast(msg, 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      addToast('Project deleted', 'success');
    } catch {
      addToast('Failed to delete project', 'error');
    }
  }

  const totalEvals = projects.reduce((sum, p) => sum + (p.evaluationCount || 0), 0);
  const scopedProjects = projects.filter(p => p.scopeKnowledge?.deliverables?.length > 0);
  const maxProjects = planMeta?.maxProjects ?? 1;
  const atLimit = maxProjects !== Infinity && projects.length >= maxProjects;

  return (
    <div className="dashboard animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Projects <span className="gradient-text">Dashboard</span></h1>
          <p className="page-title-sub">Manage your client projects and scope knowledge</p>
        </div>
        <button
          className="btn btn-primary btn-lg"
          onClick={() => atLimit ? addToast(`Your ${planMeta.label} plan allows up to ${maxProjects} project${maxProjects === 1 ? '' : 's'}. Upgrade to add more.`, 'error') : setShowNewModal(true)}
          title={atLimit ? 'Project limit reached — upgrade to add more' : 'Create a new project'}
        >
          <Plus size={16} /> New Project
        </button>
      </div>

      {/* Plan limit banner */}
      {atLimit && (
        <div className="plan-limit-banner">
          <div className="plan-limit-banner-content">
            <strong>Project limit reached</strong>
            <p>Your <strong>{planMeta.label}</strong> plan allows up to {maxProjects} project{maxProjects === 1 ? '' : 's'}. Upgrade to Pro for 5 projects or Teams for unlimited.</p>
          </div>
          <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={() => setShowUpgradeModal(true)}>
            Upgrade plan
          </button>
        </div>
      )}

      {/* Free tier usage bar */}
      {(profile?.plan || 'starter') === 'starter' && (
        <div className="usage-bar-card">
          <div className="usage-bar-header">
            <span className="usage-bar-label">Monthly scope checks</span>
            <span className="usage-bar-count">{totalEvals} / 10 used</span>
          </div>
          <div className="usage-bar-track">
            <div className="usage-bar-fill" style={{ width: `${Math.min(100, (totalEvals / 10) * 100)}%` }} />
          </div>
          {totalEvals >= 10 && (
            <p className="usage-bar-warn">Limit reached — <button className="usage-upgrade-link">upgrade to Pro for unlimited checks</button></p>
          )}
        </div>
      )}

      {/* Stats Row */}
      <div className="dashboard-stats">
        {[
          { label: 'Total Projects', value: projects.length, icon: <Layers size={18} /> },
          { label: 'Scope Loaded', value: scopedProjects.length, icon: <ListChecks size={18} /> },
          { label: 'Total Evaluations', value: totalEvals, icon: <Zap size={18} /> },
        ].map((stat, i) => (
          <div key={i} className="stat-card">
            <div className="stat-card-icon">{stat.icon}</div>
            <div className="stat-card-number">{stat.value}</div>
            <div className="stat-card-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid-auto">
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : projects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Folder size={48} /></div>
          <h3>No projects yet</h3>
          <p>Create your first project and upload a PDF contract to get started</p>
          <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
            Create First Project
          </button>
        </div>
      ) : (
        <div className="grid-auto">
          {projects.map(p => (
            <ProjectCard key={p.id} project={p} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* New Project Modal */}
      {showNewModal && (
        <div className="modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Project</h2>
              <button className="modal-close-btn" onClick={() => setShowNewModal(false)}>&#x2715;</button>
            </div>
            <form onSubmit={handleCreate} className="new-project-form">
              <div className="form-group">
                <label className="form-label" htmlFor="new-project-name">Project Name *</label>
                <input
                  id="new-project-name"
                  className="form-input"
                  placeholder="e.g. E-commerce Website Redesign"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="new-client-name">Client Name</label>
                <input
                  id="new-client-name"
                  className="form-input"
                  placeholder="e.g. Acme Corp"
                  value={newClient}
                  onChange={e => setNewClient(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-lg" disabled={creating || !newName.trim()}>
                  {creating ? <><div className="spinner" /> Creating…</> : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Upgrade to {currentPlan === 'starter' ? 'Pro' : 'Teams'}</h2>
              <button className="modal-close-btn" onClick={() => setShowUpgradeModal(false)}>&#x2715;</button>
            </div>
            <div style={{ padding: '4px 0 16px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 16 }}>
                {currentPlan === 'starter'
                  ? 'Pro gives you 5 projects, unlimited scope checks, custom tones, and the full scope ledger.'
                  : 'Teams gives you unlimited projects, a team hub, and priority support.'}
              </p>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 2 }}>Google account</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{user?.email}</div>
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 0 }}>
                We'll send upgrade instructions to this email. Payment setup follows via a secure link.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowUpgradeModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-lg" onClick={handleUpgradeRequest} disabled={submittingUpgrade}>
                {submittingUpgrade ? <><div className="spinner" /> Sending…</> : `Request Upgrade → ${targetLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
